import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getAllTDSClients, getSubClientsByClientForTDSReport, getAllFinancialYears, viewClientTDSReportGrid,
} from '../../api/ReportsAPI/clientTDSReportAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchTDSClients = createAsyncThunk('clientTDSReport/fetchClients', async (_, { rejectWithValue }) => {
    try { const res = await getAllTDSClients(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch clients')); }
});

export const fetchTDSSubClients = createAsyncThunk('clientTDSReport/fetchSubClients', async (clientcode, { rejectWithValue }) => {
    try { const res = await getSubClientsByClientForTDSReport(clientcode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub-clients')); }
});

export const fetchTDSFinancialYears = createAsyncThunk('clientTDSReport/fetchYears', async (_, { rejectWithValue }) => {
    try { const res = await getAllFinancialYears(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch financial years')); }
});

export const fetchClientTDSReport = createAsyncThunk('clientTDSReport/fetchReport', async (params, { rejectWithValue }) => {
    try { const res = await viewClientTDSReportGrid(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch client TDS report')); }
});

const initialState = {
    clientList: [], clientListLoading: false,
    subClientList: [], subClientListLoading: false,
    yearList: [], yearListLoading: false,
    report: [], reportLoading: false, reportError: null,
};

const clientTDSReportSlice = createSlice({
    name: 'clientTDSReport',
    initialState,
    reducers: {
        clearSubClients: (s) => { s.subClientList = []; },
        clearClientTDSReport: (s) => { s.report = []; s.reportError = null; },
        resetClientTDSReport: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTDSClients.pending, (s) => { s.clientListLoading = true; })
            .addCase(fetchTDSClients.fulfilled, (s, a) => { s.clientListLoading = false; s.clientList = a.payload; })
            .addCase(fetchTDSClients.rejected, (s) => { s.clientListLoading = false; });

        builder
            .addCase(fetchTDSSubClients.pending, (s) => { s.subClientListLoading = true; })
            .addCase(fetchTDSSubClients.fulfilled, (s, a) => { s.subClientListLoading = false; s.subClientList = a.payload; })
            .addCase(fetchTDSSubClients.rejected, (s) => { s.subClientListLoading = false; s.subClientList = []; });

        builder
            .addCase(fetchTDSFinancialYears.pending, (s) => { s.yearListLoading = true; })
            .addCase(fetchTDSFinancialYears.fulfilled, (s, a) => { s.yearListLoading = false; s.yearList = a.payload; })
            .addCase(fetchTDSFinancialYears.rejected, (s) => { s.yearListLoading = false; });

        builder
            .addCase(fetchClientTDSReport.pending, (s) => { s.reportLoading = true; s.reportError = null; })
            .addCase(fetchClientTDSReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
            .addCase(fetchClientTDSReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; s.report = []; });
    },
});

export const { clearSubClients, clearClientTDSReport, resetClientTDSReport } = clientTDSReportSlice.actions;

export const selectCTRClientList        = (s) => s.clientTDSReport.clientList;
export const selectCTRClientListLoading = (s) => s.clientTDSReport.clientListLoading;
export const selectCTRSubClientList     = (s) => s.clientTDSReport.subClientList;
export const selectCTRSubClientListLoading = (s) => s.clientTDSReport.subClientListLoading;
export const selectCTRYearList          = (s) => s.clientTDSReport.yearList;
export const selectCTRYearListLoading   = (s) => s.clientTDSReport.yearListLoading;
export const selectCTRReport            = (s) => s.clientTDSReport.report;
export const selectCTRReportLoading     = (s) => s.clientTDSReport.reportLoading;
export const selectCTRReportError       = (s) => s.clientTDSReport.reportError;

export default clientTDSReportSlice.reducer;
